import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import supabase from "../config/supabaseConfig";
import {
  matriculasService,
  REGRAS_AVAL,
  calcularProximaAvaliacao,
} from "../services/matriculas.service";
import "../styles/CadastroAlunoNovo.css";

// Espelha CadastroAlunoNovo (mesmo layout/DS), mas EDITA um aluno existente:
// carrega por id, salva via update e volta pro detalhe (ou pra Sala, se foi
// aberto de lá via ?from=sala — atalho da Sala, regra §4 da sprint 08/07).

// Opções (tier = campo `plano` salvo hoje). REGRAS_AVAL vem do service (fonte única).
const TIERS = ["8 Check-in", "12 Check-in", "16 Check-in", "Premium", "Black"];
const DURACOES = ["Mensal", "Semestral", "Anual"];
const NIVEIS = ["Iniciante", "Intermediário", "Avançado"];

// Duração do form ↔ formato do banco.
const formatoDe = (duracao) =>
  duracao
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
const duracaoDe = (formato) =>
  ({ mensal: "Mensal", semestral: "Semestral", anual: "Anual" }[formato] ||
    "Semestral");

const calcularIdade = (dataNascimento) => {
  if (!dataNascimento) return null;
  const hoje = new Date();
  const nasc = new Date(dataNascimento);
  let idade = hoje.getFullYear() - nasc.getFullYear();
  const m = hoje.getMonth() - nasc.getMonth();
  if (m < 0 || (m === 0 && hoje.getDate() < nasc.getDate())) idade--;
  return idade;
};

// DB guarda "Nao"/"Sim - Lesao ..."; o select usa "Não".
const lesaoDoBanco = (v) => (v === "Nao" || !v ? "Não" : v);

const EditarAluno = ({ alunoId: alunoIdProp }) => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const alunoId = alunoIdProp || params.id;
  const origemSala = searchParams.get("from") === "sala";
  // returnTo (feedback Tales 08/07): o voltar respeita a ORIGEM — a full
  // screen do aluno passa o próprio path aqui; sem ele vale a regra antiga.
  const returnTo = searchParams.get("returnTo");
  const origemFullScreen = !!returnTo && returnTo.startsWith("/aluno/");
  const destinoVoltar =
    returnTo || (origemSala ? "/sala" : `/detalhe-aluno/${alunoId}`);

  const [form, setForm] = useState({
    nome: "",
    telefone: "",
    dataNascimento: "",
    nivel: "Intermediário",
    lesao: "Não",
    tipo_lesao: "",
    objetivo: "",
    observacoes: "",
    exercicios_obrigatorios: "",
    status: "ativo",
  });
  const [tier, setTier] = useState("12 Check-in");
  const [duracao, setDuracao] = useState("Semestral"); // preview Fase 3
  // Início do contrato: usado ao trocar tier/duração — a agenda de avaliações
  // conta DESTA data. A carga abaixo preenche com o `data_inicio` gravado; o
  // "hoje" aqui é só o default de aluno SEM matrícula (legado).
  //
  // ⚠️ Bug corrigido 27/07: antes ficava SEMPRE em hoje (a carga não lia o
  // valor do banco), então a tela mentia a data de todo aluno e — pior —
  // salvar com mudança de tier/duração SOBRESCREVIA o início real do contrato
  // com a data da edição. Em prod as 18 matrículas editadas pós-go-live
  // ficaram, todas as 18, com data_inicio = o dia em que foram mexidas, e a
  // agenda de avaliações nasceu contando dali (1ª avaliação no dia da edição).
  const [inicioContrato, setInicioContrato] = useState(
    new Date().toISOString().slice(0, 10)
  );
  // Última avaliação REALIZADA — âncora da agenda rolante (27/07): a próxima
  // avaliação é esta data + o intervalo do tier. Vazio = nunca avaliado, e aí
  // não se agenda nada (o aluno entra na lista de quem precisa ser chamado).
  const [ultimaAvaliacao, setUltimaAvaliacao] = useState("");
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const [sucesso, setSucesso] = useState(false);
  // Matrícula ativa do aluno (fonte de verdade de tier/duração pós-migração).
  const [matricula, setMatricula] = useState(null);

  const set = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

  // Agenda de avaliações reativa (mensal não passa por avaliação).
  const avaliacoes = duracao === "Mensal" ? [] : REGRAS_AVAL[tier] || [];

  // Prévia da próxima avaliação (agenda rolante): última + intervalo do tier.
  const proximaAvaliacao = calcularProximaAvaliacao({
    dataUltimaAvaliacao: ultimaAvaliacao,
    formato: formatoDe(duracao),
    tierLegado: tier,
  });

  // Carregar o aluno.
  useEffect(() => {
    let ativo = true;
    (async () => {
      if (!alunoId) return;
      try {
        setCarregando(true);
        setErro(null);
        const { data, error } = await supabase
          .from("alunos")
          .select("*")
          .eq("id", alunoId)
          .single();
        if (error) throw error;
        if (!ativo) return;
        if (!data) {
          setErro("Aluno não encontrado.");
          return;
        }
        setForm({
          nome: data.nome || "",
          telefone: data.telefone || "",
          dataNascimento: data.data_nascimento || "",
          nivel: data.nivel || "Iniciante",
          lesao: lesaoDoBanco(data.lesao),
          tipo_lesao: data.tipo_lesao || "",
          objetivo: data.objetivo || "",
          observacoes: data.observacoes || "",
          exercicios_obrigatorios: data.exercicios_obrigatorios || "",
          status: data.status || "ativo",
        });
        // tier/duração: preferimos a matrícula ativa; sem ela, caímos no
        // alunos.plano legado (aluno pré-migração / sem contrato registrado).
        const mat = await matriculasService.getMatriculaAtiva(alunoId);
        if (!ativo) return;
        if (mat) {
          setMatricula(mat);
          const tl = mat.planos?.tier_legado;
          if (tl && TIERS.includes(tl)) setTier(tl);
          else if (data.plano && TIERS.includes(data.plano)) setTier(data.plano);
          setDuracao(duracaoDe(mat.formato));
          // Início REAL do contrato (fix 27/07). Sem isto o campo ficava em
          // "hoje" e o salvar sobrescrevia a data gravada.
          if (mat.data_inicio) setInicioContrato(mat.data_inicio);
          setUltimaAvaliacao(mat.data_ultima_avaliacao || "");
        } else if (data.plano && TIERS.includes(data.plano)) {
          setTier(data.plano);
        }
      } catch (err) {
        console.error("Erro ao carregar aluno:", err);
        if (ativo) setErro("Erro ao carregar dados do aluno: " + err.message);
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [alunoId]);

  const salvar = async () => {
    if (!form.nome.trim()) {
      setErro("Informe o nome do aluno.");
      return;
    }
    try {
      setSalvando(true);
      setErro(null);
      const lesaoCorrigida = form.lesao === "Não" ? "Nao" : form.lesao;
      const alunoData = {
        nome: form.nome.trim(),
        telefone: form.telefone || null,
        data_nascimento: form.dataNascimento || null,
        idade: calcularIdade(form.dataNascimento),
        status: form.status || "ativo",
        lesao: lesaoCorrigida,
        tipo_lesao: lesaoCorrigida !== "Nao" ? form.tipo_lesao || null : null,
        objetivo: form.objetivo || null,
        plano: tier,
        nivel: form.nivel || "Iniciante",
        observacoes: form.observacoes || null,
        exercicios_obrigatorios: form.exercicios_obrigatorios || null,
        // Dual-write: `alunos.plano` segue gravado na transição; a matrícula
        // ativa (tier/duração + agenda de avaliações) é atualizada abaixo.
      };
      // ORDEM DO SALVAR (07/10): matrícula PRIMEIRO, cadastro depois. Antes o
      // cadastro (alunos.plano) era gravado e a matrícula falhava em seguida —
      // a tela dizia um plano e a matrícula outro (11 alunos em prod). Agora:
      //  1. matrícula (é a parte que pode ser barrada); se falhar, nada foi
      //     gravado;
      //  2. cadastro; se falhar, a matrícula volta ao que era;
      //  3. agenda da próxima avaliação — se só ela falhar, os dados já estão
      //     consistentes e o aviso diz isso.
      const formatoNovo = formatoDe(duracao);
      let matSalva = null;
      let desfazerMatricula = null;

      if (matricula) {
        const tierMudou = tier !== matricula.planos?.tier_legado;
        const formatoMudou = formatoNovo !== matricula.formato;
        const inicioMudou =
          (inicioContrato || null) !== (matricula.data_inicio || null);
        const avaliacaoMudou =
          (ultimaAvaliacao || null) !==
          (matricula.data_ultima_avaliacao || null);
        if (tierMudou || formatoMudou || inicioMudou || avaliacaoMudou) {
          matSalva = await matriculasService.atualizarMatricula(matricula.id, {
            tierLegado: tier,
            formato: formatoNovo,
            // Data real do contrato: o campo já vem carregado com o
            // `data_inicio` gravado (fix 27/07), então salvar sem mexer nele
            // PRESERVA a data. É histórico — não ancora mais a agenda.
            dataInicio: inicioContrato || matricula.data_inicio,
            dataUltimaAvaliacao: ultimaAvaliacao || null,
          });
          desfazerMatricula = () =>
            matriculasService.atualizarMatricula(matricula.id, {
              tierLegado: matricula.planos?.tier_legado,
              formato: matricula.formato,
              dataInicio: matricula.data_inicio,
              dataUltimaAvaliacao: matricula.data_ultima_avaliacao || null,
            });
        }
      } else {
        // Aluno sem matrícula (legado, ou cadastro novo cuja matrícula falhou):
        // cria agora — antes só gravava alunos.plano e a matrícula nunca
        // nascia, apesar do aviso do cadastro mandar "editar e salvar".
        matSalva = await matriculasService.criarMatricula({
          alunoId,
          tierLegado: tier,
          formato: formatoNovo,
          dataInicio: inicioContrato,
        });
        if (ultimaAvaliacao) {
          matSalva = await matriculasService.atualizarMatricula(matSalva.id, {
            tierLegado: tier,
            formato: formatoNovo,
            dataInicio: inicioContrato,
            dataUltimaAvaliacao: ultimaAvaliacao,
          });
        }
        // Sem desfazer: apagar matrícula é só admin. Se o cadastro falhar
        // depois, a matrícula criada já reflete o plano escolhido.
      }

      const { error } = await supabase
        .from("alunos")
        .update(alunoData)
        .eq("id", alunoId);
      if (error) {
        if (desfazerMatricula) {
          try {
            await desfazerMatricula();
          } catch (errDesfazer) {
            console.error("Erro ao desfazer matrícula:", errDesfazer);
          }
        }
        throw error;
      }

      if (matSalva) {
        // Agenda ROLANTE (27/07): a próxima avaliação sai de
        // `data_ultima_avaliacao + intervalo do tier`. Não existe mais
        // geração automática a partir da data do contrato — era ela que
        // criava avaliação fantasma já atrasada a cada edição.
        try {
          await matriculasService.agendarProximaAvaliacao(matSalva);
        } catch (errAgenda) {
          console.error("Erro ao agendar próxima avaliação:", errAgenda);
          setErro(
            "Aluno e plano salvos, mas a próxima avaliação não foi agendada: " +
              errAgenda.message
          );
          return;
        }
      }

      setSucesso(true);
      setTimeout(() => navigate(destinoVoltar), 900);
    } catch (err) {
      console.error("Erro ao atualizar aluno:", err);
      setErro("Erro ao atualizar aluno: " + err.message);
    } finally {
      setSalvando(false);
    }
  };

  const voltar = () => navigate(destinoVoltar);

  const Pills = ({ opcoes, valor, onChange }) => (
    <div className="ca-pillset">
      {opcoes.map((op) => (
        <button
          type="button"
          key={op}
          className={`ca-pill ${op === valor ? "active" : ""} ${
            op === "Black" ? "dark" : ""
          }`}
          onClick={() => onChange(op)}
        >
          {op}
        </button>
      ))}
    </div>
  );

  if (carregando) {
    return (
      <div className="cadastro-aluno-novo">
        <div className="ca-header">
          <h1>Editar Aluno</h1>
        </div>
        <p className="ca-note">Carregando dados do aluno…</p>
      </div>
    );
  }

  return (
    <div className="cadastro-aluno-novo">
      <div className="ca-topbar">
        <div className="ca-header">
          <h1>Editar Aluno</h1>
          <span className="ca-sub">{form.nome || "—"}</span>
        </div>
        <div className="ca-topbar-acoes">
          {/* Acesso às cargas direto da edição (feedback Tales 07/07). */}
          <button
            type="button"
            className="ca-btn-voltar-sala ca-btn-cargas"
            onClick={() =>
              // Propaga a origem: Cargas volta pro mesmo lugar que este form.
              navigate(
                `/cargas/${alunoId}${
                  returnTo
                    ? `?returnTo=${encodeURIComponent(returnTo)}`
                    : origemSala
                    ? "?from=sala"
                    : ""
                }`
              )
            }
          >
            Editar Cargas
          </button>
          {(origemSala || returnTo) && (
            <button
              type="button"
              className="ca-btn-voltar-sala"
              onClick={voltar}
            >
              {origemFullScreen ? "← Voltar ao aluno" : "← Voltar para Sala"}
            </button>
          )}
        </div>
      </div>

      {erro && <div className="ca-erro">{erro}</div>}
      {sucesso && (
        <div className="ca-erro ca-sucesso">Alterações salvas com sucesso!</div>
      )}

      <div className="ca-grid">
        {/* Coluna do formulário */}
        <div className="ca-card">
          <div className="ca-section-title">Dados básicos</div>
          <div className="ca-two">
            <label className="ca-field">
              <span>Nome completo*</span>
              <input
                type="text"
                value={form.nome}
                onChange={(e) => set("nome", e.target.value)}
                placeholder="Nome do aluno"
              />
            </label>
            <label className="ca-field">
              <span>Telefone (WhatsApp)</span>
              <input
                type="tel"
                value={form.telefone}
                onChange={(e) => set("telefone", e.target.value)}
                placeholder="(24) 9____-____"
              />
            </label>
          </div>
          <div className="ca-two">
            <label className="ca-field">
              <span>Data de nascimento</span>
              <input
                type="date"
                value={form.dataNascimento}
                onChange={(e) => set("dataNascimento", e.target.value)}
              />
            </label>
            <label className="ca-field">
              <span>Status</span>
              <select
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
              >
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </select>
            </label>
          </div>

          <hr className="ca-hr" />
          <div className="ca-section-title">Plano — tier</div>
          <p className="ca-note">Define o número de avaliações.</p>
          <Pills opcoes={TIERS} valor={tier} onChange={setTier} />

          <div className="ca-section-title ca-mt">Plano — duração</div>
          <p className="ca-note">
            Define o intervalo do contrato. Mensal não passa por avaliação.
          </p>
          <Pills opcoes={DURACOES} valor={duracao} onChange={setDuracao} />

          <label className="ca-field ca-mt">
            <span>Início do contrato</span>
            <input
              type="date"
              value={inicioContrato}
              onChange={(e) => setInicioContrato(e.target.value)}
            />
          </label>
          <p className="ca-note">
            Data em que o contrato começou. É histórico — não define a agenda de
            avaliações.
          </p>

          <label className="ca-field ca-mt">
            <span>Última avaliação realizada</span>
            <input
              type="date"
              value={ultimaAvaliacao}
              onChange={(e) => setUltimaAvaliacao(e.target.value)}
            />
          </label>
          <p className="ca-note">
            {!ultimaAvaliacao ? (
              "Em branco = aluno nunca avaliado. Nada é agendado até esta data ser informada."
            ) : !proximaAvaliacao ? (
              "Este plano não gera reavaliação automática."
            ) : (
              <>
                Próxima avaliação:{" "}
                {proximaAvaliacao.split("-").reverse().join("/")}
                {proximaAvaliacao < new Date().toISOString().slice(0, 10) && (
                  <strong className="ca-aval-vencida"> — ATRASADA</strong>
                )}
              </>
            )}
          </p>

          <div className="ca-section-title ca-mt">Nível</div>
          <Pills
            opcoes={NIVEIS}
            valor={form.nivel}
            onChange={(v) => set("nivel", v)}
          />

          <div className="ca-section-title ca-mt">Lesão / restrição</div>
          <select
            className="ca-select"
            value={form.lesao}
            onChange={(e) => set("lesao", e.target.value)}
          >
            <option value="Não">Não</option>
            <option value="Sim - Lesao Moderada">Sim - Lesão Moderada</option>
            <option value="Sim - Lesao Grave">Sim - Lesão Grave</option>
          </select>
          {form.lesao !== "Não" && (
            <label className="ca-field ca-mt">
              <span>Descrição da lesão</span>
              <textarea
                rows={2}
                value={form.tipo_lesao}
                onChange={(e) => set("tipo_lesao", e.target.value)}
                placeholder="Ex.: ombro direito, em avaliação"
              />
            </label>
          )}

          <label className="ca-field ca-mt">
            <span>Objetivo de treino</span>
            <textarea
              rows={2}
              value={form.objetivo}
              onChange={(e) => set("objetivo", e.target.value)}
              placeholder="Ex.: hipertrofia, condicionamento…"
            />
          </label>
          {/* Feedback Tales 08/07: "Observações" virou "Treino" (mesma
              coluna alunos.observacoes — dados antigos continuam aqui) e
              "Exercícios obrigatórios" é campo novo logo abaixo. Ambos
              espelham na full screen do aluno, preservando parágrafos. */}
          <label className="ca-field ca-mt">
            <span>Treino</span>
            <textarea
              rows={4}
              value={form.observacoes}
              onChange={(e) => set("observacoes", e.target.value)}
              placeholder="Treino do aluno — use ENTER para separar parágrafos"
            />
          </label>
          <label className="ca-field ca-mt">
            <span>Exercícios obrigatórios</span>
            <textarea
              rows={3}
              value={form.exercicios_obrigatorios}
              onChange={(e) => set("exercicios_obrigatorios", e.target.value)}
              placeholder="Ex.: agachamento livre, prancha 3×40s…"
            />
          </label>

          <div className="ca-actions">
            <button
              type="button"
              className="ca-btn-ghost"
              onClick={voltar}
              disabled={salvando}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="ca-btn-primary"
              onClick={salvar}
              disabled={salvando}
            >
              {salvando ? "Salvando…" : "Salvar alterações"}
            </button>
          </div>
        </div>

        {/* Coluna lateral — agenda de avaliações (preview) + regras */}
        <div>
          <div className="ca-card ca-card-dark">
            <div className="ca-overline">Agenda de avaliações</div>
            <div className="ca-plano-sel">
              {tier} · {duracao}
            </div>
            {avaliacoes.length > 0 ? (
              avaliacoes.map((a) => (
                <div key={a.n} className="ca-aval-item">
                  <div className="ca-aval-num">{a.n}</div>
                  <div className="ca-aval-when">
                    {a.q}
                    <small>{a.w}</small>
                  </div>
                </div>
              ))
            ) : (
              <p className="ca-note">
                Plano mensal não passa por avaliação automática. A avaliação é
                feita à parte.
              </p>
            )}
          </div>

          <div className="ca-card ca-mt">
            <div className="ca-section-title">Regras (referência)</div>
            <div className="ca-regras">
              8 Check-in → 1 (entrada)
              <br />
              12 Check-in → 2 (entrada + 4 meses)
              <br />
              16 Check-in → 3 (a cada 3 meses)
              <br />
              Premium → 4 (entrada + 3× a cada 3 meses)
              <br />
              Black → 4 (entrada + 3× a cada 3 meses)
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditarAluno;
